import { Box, Typography } from '@mui/material';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SubscriptionStatus } from 'shared/types/Subscription';

import { RightArrowIcon } from '@/assets/svg';
import PremiumStarIcon from '@/assets/svg/PremiumStarIcon';
import { Button, Modal } from '@/components';
import { useOrganisationsContext } from '@/context/OrganisationsContext';
import { useSubscriptionContext } from '@/context/SubscriptionContext';
import { useIsPremiumFeatureEnabled } from '@/hooks';
import { SwitchTeam } from '@/pages/PremiumPage/components';
import { ROUTE } from '@/router/routes';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';

export const SubscriptionExpireBanner: React.FC = () => {
    const [isSwitchTeamModalOpen, setIsSwitchTeamModalOpen] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { userSubscriptions } = useSubscriptionContext();
    const { userOrganisation, userOrganisations } = useOrganisationsContext();
    const { isPremiumFeatureEnabled } = useIsPremiumFeatureEnabled();

    const subscriptionGoingToExpire = useMemo(() => {
        return userSubscriptions.find(
            (sub) =>
                (sub?.status === SubscriptionStatus.INTERNAL_TRIAL ||
                    sub?.status === SubscriptionStatus.SUSPENDED ||
                    sub?.status === SubscriptionStatus.GRACE_PERIOD) &&
                userOrganisations?.find(
                    (org) => org.id === sub?.organisation_id,
                )?.owner,
        );
    }, [userSubscriptions, userOrganisations]);

    const subscriptionDaysLeft = useMemo(() => {
        if (!subscriptionGoingToExpire?.valid_until) return null;

        const today = moment().startOf('day');
        const until = moment(subscriptionGoingToExpire.valid_until).startOf(
            'day',
        );

        const now = moment();

        if (moment(subscriptionGoingToExpire.valid_until).isBefore(now)) {
            return -1;
        }

        return until.diff(today, 'days');
    }, [subscriptionGoingToExpire?.valid_until]);

    const isTeamSubscription =
        subscriptionGoingToExpire?.current_plan?.includes('team');

    if (
        subscriptionDaysLeft === null ||
        subscriptionDaysLeft < 0 ||
        subscriptionDaysLeft > 3 ||
        !subscriptionGoingToExpire ||
        !isPremiumFeatureEnabled
    ) {
        return null;
    }

    const titleText =
        subscriptionDaysLeft === 0
            ? t('SubscriptionExpireBanner:titleToday')
            : t('SubscriptionExpireBanner:titleInDays', {
                  count: subscriptionDaysLeft,
              });

    const onButtonClick = () => {
        if (
            userOrganisation?.id === subscriptionGoingToExpire?.organisation_id
        ) {
            navigate(ROUTE.premium);
        } else {
            setIsSwitchTeamModalOpen(true);
        }
    };

    return (
        <>
            <Modal
                isOpen={isSwitchTeamModalOpen}
                center
                styles="p-8 max-w-[515px] w-full"
            >
                <SwitchTeam onClose={() => setIsSwitchTeamModalOpen(false)} />
            </Modal>

            <Box
                sx={{
                    backgroundColor: COLOR.accountSetupBlue,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        maxWidth: '1200px',
                        margin: 'auto',
                        p: '14px',
                        gap: 1,
                    }}
                >
                    <PremiumStarIcon
                        width={20}
                        height={20}
                        color={
                            isTeamSubscription
                                ? ENTITY_COLOR.teams
                                : COLOR.deepCerulean
                        }
                        secondaryColor={COLOR.white}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: COLOR.badgeTextGrey,
                                fontWeight: 700,
                            }}
                        >
                            {titleText}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: COLOR.paleSky,
                                    fontWeight: 300,
                                }}
                            >
                                {t('SubscriptionExpireBanner:desc')}
                            </Typography>
                            <Button
                                sx={{
                                    color: COLOR.deepCerulean,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    height: 24,
                                    py: 0,
                                }}
                                variant="text"
                                caption={t('Actions:learn_more')}
                                endIcon={
                                    <RightArrowIcon
                                        color={COLOR.deepCerulean}
                                        width={16}
                                        height={16}
                                    />
                                }
                                onClick={onButtonClick}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
};
